import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { usePreventScrollShift } from "./hooks/usePreventScrollShift";
import { filtrarCambiosParaCliente } from "./permisos";
import StandardModal from "./StandardModal";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Wallet,
  Building2,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  MapPin,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  ChevronDown,
  Settings,
  ArrowRightLeft,
  RefreshCw, // <- Agregar RefreshCw aquí
  Package,
} from "lucide-react";

// ===== CONFIGURACIÓN ANTI-ZOOM OBLIGATORIA =====
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// ===== SISTEMA DE COLORES POR VISTA =====
const VISTA_COLORS = {
  cambios: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    solid: "#2563eb",
    light: "#2563eb10",
    description: "Cambio de monedas",
  },
};

const getVistaColor = (vistaId) => {
  return VISTA_COLORS[vistaId] || VISTA_COLORS.cambios;
};

// ===== ESPACIADO UNIFICADO =====
const SPACING = {
  containerPadding: (isMobile) => (isMobile ? "16px 12px 120px 12px" : "20px"),
  sectionMargin: "20px",
  headerMargin: "16px",
  cardPadding: "16px",
  cardGap: "8px",
  statsGap: "12px",
  buttonGap: "12px",
  formGap: "16px",
  borderRadius: {
    small: "6px",
    medium: "12px",
    large: "16px",
    xlarge: "20px",
  },
};

// Funciones helper para manejar fechas sin problemas de timezone
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";

  // Tomar directamente los componentes del string YYYY-MM-DD
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

const formatMonthForSelect = (monthString) => {
  // monthString formato: "2025-01"
  const [year, month] = monthString.split("-");
  const date = new Date(year, month - 1, 15); // Día 15 para evitar problemas de timezone
  return new Intl.DateTimeFormat("es-ES", {
    year: "numeric",
    month: "long",
  }).format(date);
};

// ===== DROPDOWN PERSONALIZADO UNIFICADO =====
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
  onSearch,
  canCreate = false,
  onCreateNew,
  createText = "Crear nuevo",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {/* Campo de búsqueda */}
          <div style={{ padding: "8px" }}>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  canCreate &&
                  filteredOptions.length === 0 &&
                  searchTerm.trim()
                ) {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }
              }}
            />
          </div>

          {/* Opciones filtradas */}
          {filteredOptions.map((option, index) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
                setSearchTerm("");
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {option.label}
            </button>
          ))}

          {/* Opción de crear nuevo */}
          {canCreate && searchTerm.trim() && filteredOptions.length === 0 && (
            <button
              onClick={() => {
                onCreateNew?.(searchTerm.trim());
                setSearchTerm("");
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "#f0fdf4",
                color: "#166534",
                fontSize: "14px",
                fontWeight: "600",
                textAlign: "left",
                cursor: "pointer",
                borderTop: "1px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                touchAction: "manipulation",
              }}
            >
              <Plus size={14} />
              {createText}: "{searchTerm}"
            </button>
          )}

          {/* Mensaje cuando no hay resultados */}
          {!canCreate && filteredOptions.length === 0 && searchTerm && (
            <div
              style={{
                padding: "16px",
                textAlign: "center",
                color: "#64748b",
                fontSize: "14px",
              }}
            >
              No se encontraron resultados
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#3b82f6",
          text: "#1d4ed8",
          button: "#3b82f6",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {type === "confirm" && (
              <AlertCircle size={32} color={colors.button} />
            )}
            {type === "error" && <XCircle size={32} color={colors.button} />}
            {type === "success" && (
              <CheckCircle2 size={32} color={colors.button} />
            )}
            {type === "info" && <Info size={32} color={colors.button} />}
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para vista completa del cambio
function CambioDetailModal({
  cambio,
  proyectos,
  cajas,
  proveedoresCambio,
  onClose,
  isMobile,
}) {
  const proyecto = proyectos.find((p) => p.id === cambio.proyecto_id);
  const proveedor = proveedoresCambio.find(
    (p) => p.id === cambio.proveedor_cambio_id
  );
  const cajaOrigen = cajas.find((c) => c.id === cambio.caja_origen_id);
  const cajaDestino = cajas.find((c) => c.id === cambio.caja_destino_id);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: getVistaColor("cambios").gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Eye size={20} color="white" />
            </div>
            <div>
              <h2
                style={{
                  margin: "0 0 4px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                }}
              >
                Vista Detallada - Cambio
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                }}
              >
                {proyecto?.codigo} - {formatDateForDisplay(cambio.fecha)}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "24px",
            }}
          >
            {/* Información Principal */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={18} />
                Información Principal
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Proyecto */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Proyecto
                  </p>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {proyecto?.codigo}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Cliente: {proyecto?.apellido_cliente}
                  </p>
                </div>

                {/* Proveedor */}
                <div
                  style={{
                    background: getVistaColor("cambios").light,
                    padding: "16px",
                    borderRadius: "12px",
                    border: `2px solid ${getVistaColor("cambios").solid}30`,
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: getVistaColor("cambios").solid,
                      textTransform: "uppercase",
                    }}
                  >
                    Proveedor de cambio
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: getVistaColor("cambios").solid,
                    }}
                  >
                    {proveedor?.nombre}
                  </p>
                </div>

                {/* Operación de Cambio */}
                <div
                  style={{
                    background: getVistaColor("cambios").gradient,
                    padding: "20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    color: "white",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      opacity: 0.9,
                    }}
                  >
                    Operación de Cambio
                  </p>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "12px",
                      fontSize: isMobile ? "16px" : "20px",
                      fontWeight: "700",
                      margin: "8px 0",
                    }}
                  >
                    <span style={{ color: "#fecaca" }}>
                      {cambio.moneda_origen}{" "}
                      {Number(cambio.monto_origen).toLocaleString()}
                    </span>
                    <ArrowRightLeft size={16} style={{ opacity: 0.8 }} />
                    <span style={{ color: "#bbf7d0" }}>
                      {cambio.moneda_destino}{" "}
                      {Number(cambio.monto_destino).toLocaleString()}
                    </span>
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      opacity: 0.9,
                      fontWeight: "600",
                    }}
                  >
                    Tipo de cambio: $
                    {Number(cambio.tipo_cambio).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Información Adicional */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={18} />
                Detalles Adicionales
              </h3>

              <div style={{ display: "grid", gap: "16px" }}>
                {/* Fecha */}
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #e2e8f0",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 6px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#64748b",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                    }}
                  >
                    {formatDateForDisplay(cambio.fecha)}
                  </p>
                </div>

                {/* Cajas involucradas */}
                <div
                  style={{
                    background: "#eff6ff",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "2px solid #dbeafe",
                  }}
                >
                  <p
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "12px",
                      fontWeight: "600",
                      color: "#1e40af",
                      textTransform: "uppercase",
                    }}
                  >
                    Cajas Involucradas
                  </p>

                  <div style={{ marginBottom: "12px" }}>
                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1e40af",
                      }}
                    >
                      Caja Origen
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Wallet size={14} />
                      {cajaOrigen?.nombre} {cajaOrigen?.moneda}
                    </p>
                  </div>

                  <div>
                    <p
                      style={{
                        margin: "0 0 4px 0",
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#1e40af",
                      }}
                    >
                      Caja Destino
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Wallet size={14} />
                      {cajaDestino?.nombre} {cajaDestino?.moneda}
                    </p>
                  </div>
                </div>

                {/* Descripción */}
                {cambio.descripcion && (
                  <div
                    style={{
                      background: "#f8fafc",
                      padding: "16px",
                      borderRadius: "12px",
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#64748b",
                        textTransform: "uppercase",
                      }}
                    >
                      Descripción
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {cambio.descripcion}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Documento adjunto */}
          {cambio.tiene_comprobante && cambio.comprobante_url && (
            <div
              style={{
                marginTop: "32px",
              }}
            >
              <h3
                style={{
                  margin: "0 0 20px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={18} />
                Comprobante de Cambio
              </h3>

              <div
                style={{
                  background: "white",
                  border: "2px solid #22c55e",
                  borderRadius: "12px",
                  overflow: "hidden",
                  maxWidth: "400px",
                }}
              >
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    color: "white",
                    padding: "12px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <FileText size={16} />
                    Comprobante
                  </span>
                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={() => {
                        const proyecto = proyectos.find(
                          (p) => p.id === cambio.proyecto_id
                        );
                        const fileName = `comprobante_cambio_${
                          proyecto?.codigo || "cambio"
                        }_${formatDateForDisplay(cambio.fecha).replace(
                          /\//g,
                          "-"
                        )}.${cambio.comprobante_url.split(".").pop()}`;

                        const a = document.createElement("a");
                        a.href = cambio.comprobante_url;
                        a.download = fileName;
                        a.target = "_blank";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 6px",
                        color: "white",
                        fontSize: "10px",
                        cursor: "pointer",
                        fontWeight: "600",
                      }}
                    >
                      <Download size={12} />
                    </button>
                    <button
                      onClick={() =>
                        window.open(cambio.comprobante_url, "_blank")
                      }
                      style={{
                        background: "rgba(255,255,255,0.2)",
                        border: "none",
                        borderRadius: "4px",
                        padding: "4px 6px",
                        color: "white",
                        fontSize: "10px",
                        cursor: "pointer",
                        fontWeight: "600",
                      }}
                    >
                      <Eye size={12} />
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    height: "200px",
                    background: "#000",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => window.open(cambio.comprobante_url, "_blank")}
                >
                  {cambio.comprobante_url.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i
                  ) ? (
                    <img
                      src={cambio.comprobante_url}
                      alt="Comprobante"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        color: "white",
                        textAlign: "center",
                      }}
                    >
                      <FileText
                        size={32}
                        color="white"
                        style={{ marginBottom: "8px" }}
                      />

                      <p style={{ fontSize: "12px", margin: 0 }}>
                        Vista previa no disponible
                      </p>
                      <p
                        style={{
                          fontSize: "10px",
                          margin: "4px 0 0 0",
                          opacity: 0.8,
                        }}
                      >
                        Click para abrir
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Modal simple para visualizar documento individual
function DocumentViewModal({ url, fileName, onClose, isMobile }) {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download =
        fileName || `comprobante_${Date.now()}.${url.split(".").pop()}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error descargando archivo:", error);
    }
  };

  const isImage = url.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isPDF = url.match(/\.pdf$/i);

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2500,
        padding: "10px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "12px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "90vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header simple */}
        <div
          style={{
            background: "#22c55e",
            color: "white",
            padding: "16px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FileText size={16} />
            Comprobante de Cambio
          </h3>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDownload}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Download size={14} />
              Descargar
            </button>
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "6px",
                padding: "8px 12px",
                color: "white",
                fontSize: "14px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Contenido del documento */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#000",
            minHeight: isMobile ? "400px" : "500px",
          }}
        >
          {isImage ? (
            <img
              src={url}
              alt="Comprobante"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          ) : isPDF ? (
            <iframe
              src={url}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                minHeight: "500px",
              }}
              title="Comprobante"
            />
          ) : (
            <div
              style={{
                color: "white",
                textAlign: "center",
                padding: "40px",
              }}
            >
              <FileText
                size={64}
                color="white"
                style={{ marginBottom: "16px" }}
              />
              <p style={{ fontSize: "16px", marginBottom: "20px" }}>
                Archivo no previsualizable
              </p>
              <button
                onClick={handleDownload}
                style={{
                  background: "#22c55e",
                  color: "white",
                  border: "none",
                  padding: "12px 24px",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Descargar archivo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Cambios({ constructora, usuario, permisosCliente }) {
  const [cambios, setCambios] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [proveedoresCambio, setProveedoresCambio] = useState([]); // solo activos
  const [todosLosProveedoresCambio, setTodosLosProveedoresCambio] = useState([]); // todos para visualización
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isClient, setIsClient] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCambio, setEditingCambio] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProject, setFilterProject] = useState(() => {
    // Intentar restaurar el último filtro de proyecto desde localStorage
    try {
      const saved = localStorage.getItem('cambios_filterProject');
      return saved || "todos";
    } catch (error) {
      console.error('Error restaurando filtro de proyecto:', error);
      return "todos";
    }
  });
  const [filterMonth, setFilterMonth] = useState("todos");
  const [filterEstado, setFilterEstado] = useState('activos');

  // Estados para el sistema de filtros con engranaje
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Estados para datos filtrados
  const [cambiosFiltrados, setCambiosFiltrados] = useState([]);

  // Estados para modales
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCambioDetail, setSelectedCambioDetail] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Determinar si es cliente y sus permisos
  const isCliente = usuario?.tipo_usuario === "cliente";
  const isConstructoraOrEmpleado =
    usuario?.tipo_usuario === "constructora" ||
    usuario?.tipo_usuario === "empleado";

  // Función para detectar si hay filtros activos
  const hayFiltrosActivos = () => {
    return filterProject !== "todos" || 
           filterMonth !== "todos" || 
           filterEstado !== "activos";
  };
  const puedeEditar = isConstructoraOrEmpleado;
  const puedeCrear = isConstructoraOrEmpleado;
  const puedeEliminar = isConstructoraOrEmpleado;

  // Configuración de vista
  const vistaActual = "cambios";
  const colorVista = getVistaColor(vistaActual);

  useEffect(() => {
    setIsClient(true);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (constructora?.id) {
      cargarDatos();
    }
  }, [constructora, filterEstado]);

  // Aplicar filtros cuando cambian los datos o permisos
  useEffect(() => {
    aplicarFiltros();
  }, [cambios, searchTerm, filterProject, filterMonth, permisosCliente]);

  // Función para guardar el filtro de proyecto en localStorage
  const guardarFiltroProyecto = (filtro) => {
    try {
      localStorage.setItem('cambios_filterProject', filtro);
    } catch (error) {
      console.error('Error guardando filtro de proyecto:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // Función para abrir vista detallada
  const openDetailModal = (cambio) => {
    setSelectedCambioDetail(cambio);
    setShowDetailModal(true);
  };

  // Función para abrir documento individual
  const openDocumentModal = (url, cambio) => {
    const proyecto = proyectos.find((p) => p.id === cambio.proyecto_id);
    const fileName = `comprobante_cambio_${
      proyecto?.codigo || "cambio"
    }_${formatDateForDisplay(cambio.fecha).replace(/\//g, "-")}.${url
      .split(".")
      .pop()}`;

    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  const cargarDatos = async () => {
    try {
      // Cargar cambios con datos relacionados (LEFT para conservar nombres aunque el proveedor esté inactivo)
      let queryCambios = supabase
        .from("cambios_moneda")
        .select(
          `
          *,
          proyectos (codigo, apellido_cliente),
          proveedores_cambio!left (nombre),
          caja_origen:cajas!cambios_moneda_caja_origen_id_fkey (
            id,
            nombre, 
            tipo_caja, 
            moneda,
            proyectos (codigo)
          ),
          caja_destino:cajas!cambios_moneda_caja_destino_id_fkey (
            id,
            nombre, 
            tipo_caja, 
            moneda,
            proyectos (codigo)
          )
        `
        )
        .eq("constructora_id", constructora.id);

      if (filterEstado === 'activos') {
        queryCambios = queryCambios.eq('activo', true).is('deleted_at', null);
      } else if (filterEstado === 'eliminados') {
        queryCambios = queryCambios.or('activo.eq.false,deleted_at.not.is.null');
      }

      const { data: cambiosData, error: cambiosError } = await queryCambios.order("fecha", { ascending: false });

      if (cambiosError) {
        console.error("Error cargando cambios:", cambiosError);
      } else {
        setCambios(cambiosData || []);
      }

      // Cargar proyectos según permisos
      let proyectosData = [];
      let proyectosError = null;

      if (filterEstado === 'eliminados') {
        const { data: idsData, error: idsError } = await supabase
          .from('cambios_moneda')
          .select('proyecto_id')
          .eq('constructora_id', constructora.id)
          .or('activo.eq.false,deleted_at.not.is.null');

        if (idsError) {
          proyectosError = idsError;
        } else {
          const ids = Array.from(new Set((idsData || []).map(r => r.proyecto_id).filter(Boolean)));
          let query = supabase
            .from('proyectos')
            .select('*')
            .eq('constructora_id', constructora.id);

          if (isCliente && permisosCliente?.proyectoId) {
            if (ids.includes(permisosCliente.proyectoId)) {
              query = query.eq('id', permisosCliente.proyectoId);
            } else {
              proyectosData = [];
              query = null;
            }
          } else if (ids.length > 0) {
            query = query.in('id', ids).order('codigo');
          } else {
            proyectosData = [];
            query = null;
          }

          if (query) {
            const { data, error } = await query;
            proyectosData = data || [];
            proyectosError = error || null;
          }
        }
      } else {
        let query = supabase
          .from('proyectos')
          .select('*')
          .eq('constructora_id', constructora.id)
          .eq('activo', true)
          .is('deleted_at', null);

        if (isCliente && permisosCliente?.proyectoId) {
          query = query.eq('id', permisosCliente.proyectoId);
        } else {
          query = query.order('codigo');
        }

        const { data, error } = await query;
        proyectosData = data || [];
        proyectosError = error || null;
      }

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);

        // Si es cliente, preseleccionar su proyecto
        if (isCliente && permisosCliente?.proyectoId) {
          setFilterProject(permisosCliente.proyectoId);
        }
      }

      // Cargar cajas
      const { data: cajasData, error: cajasError } = await supabase
        .from("cajas")
        .select(
          `
          *,
          proyectos (codigo)
        `
        )
        .eq("constructora_id", constructora.id)
        .order("tipo_caja");

      if (cajasError) {
        console.error("Error cargando cajas:", cajasError);
      } else {
        setCajas(cajasData || []);
      }

      // Cargar proveedores de cambio (activos)
      const { data: proveedoresData, error: proveedoresError } = await supabase
        .from("proveedores_cambio")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (proveedoresError) {
        console.error("Error cargando proveedores cambio:", proveedoresError);
      } else {
        setProveedoresCambio(proveedoresData || []);
      }

      // Cargar todos los proveedores (para mostrar nombres históricos y permitir que el seleccionado aparezca en dropdown)
      const { data: todosProvData, error: todosProvError } = await supabase
        .from("proveedores_cambio")
        .select("*")
        .eq("constructora_id", constructora.id)
        .order("nombre");
      if (todosProvError) {
        console.error("Error cargando todos los proveedores cambio:", todosProvError);
      } else {
        setTodosLosProveedoresCambio(todosProvData || []);
      }
    } catch (err) {
      console.error("Error:", err);
    }
    setLoading(false);
  };

  // Función para aplicar todos los filtros
  const aplicarFiltros = () => {
    let cambiosParaProcesar = [...cambios];

    // 1. FILTROS DE PERMISOS: Aplicar filtros de cliente primero
    if (isCliente && permisosCliente) {
      cambiosParaProcesar = filtrarCambiosParaCliente(
        cambiosParaProcesar,
        permisosCliente,
        cajas
      );
    }

    // 2. FILTROS DE BÚSQUEDA: Aplicar filtros de interfaz
    const cambiosFiltradosResult = cambiosParaProcesar.filter((cambio) => {
      // Filtro de búsqueda por texto
      const matchesSearch =
        cambio.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cambio.proyectos?.codigo
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        cambio.proyectos?.apellido_cliente
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        cambio.proveedores_cambio?.nombre
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      // Filtro por proyecto (solo para constructoras, clientes ya están filtrados)
      const matchesProject =
        isCliente ||
        filterProject === "todos" ||
        cambio.proyecto_id === filterProject;

      // Filtro por mes
      const matchesMonth =
        filterMonth === "todos" || cambio.fecha.substring(0, 7) === filterMonth;

      return matchesSearch && matchesProject && matchesMonth;
    });

    setCambiosFiltrados(cambiosFiltradosResult);
  };

  const handleEliminar = async (id) => {
    if (!puedeEliminar) {
      showCustomAlert(
        "error",
        "Sin permisos",
        "No tienes permisos para eliminar cambios"
      );
      return;
    }

    showCustomAlert(
      'confirm',
      '¿Eliminar cambio?',
      'Se revertirá el impacto en cajas (sumar origen, restar destino) y el cambio quedará eliminado (recuperable). ¿Continuar?',
      async () => {
        try {
                  const { error } = await supabase.rpc('rpc_soft_delete_cambio', {
          p_cambio_id: id,
          p_usuario_id: usuario.id,
          p_razon: null,
        });
          if (error) {
            showCustomAlert('error', 'Error', 'Error al eliminar: ' + error.message);
          } else {
            await cargarDatos();
            showCustomAlert('success', 'Éxito', 'Cambio eliminado correctamente');
          }
        } catch (err) {
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
        }
      }
    );
  };

  const handleRestaurar = async (id) => {
    showCustomAlert(
      'confirm',
      '¿Restaurar cambio?',
      'Se re-aplicará el impacto en cajas (restar origen, sumar destino). ¿Continuar?',
      async () => {
        try {
                  const { error } = await supabase.rpc('rpc_restore_cambio', {
          p_cambio_id: id,
          p_usuario_id: usuario.id,
        });
          if (error) {
            showCustomAlert('error', 'Error', 'Error al restaurar: ' + error.message);
          } else {
            await cargarDatos();
            showCustomAlert('success', 'Éxito', 'Cambio restaurado correctamente');
          }
        } catch (err) {
          showCustomAlert('error', 'Error', 'Error: ' + err.message);
        }
      }
    );
  };

  // Obtener meses únicos de cambios filtrados, no todos
  const mesesUnicos = [
    ...new Set(cambiosFiltrados.map((c) => c.fecha.substring(0, 7))),
  ]
    .sort()
    .reverse();

  // Generar nombre de caja para mostrar
  const generarNombreCaja = (caja) => {
    if (!caja) return "Caja eliminada";
    return `${caja.nombre} ${caja.moneda}`;
  };

  // Calcular totales para resumen basado en datos filtrados
  const cambiosARStoUSD = cambiosFiltrados.filter(
    (c) => c.moneda_origen === "ARS" && c.moneda_destino === "USD"
  );
  const cambiosUSDtoARS = cambiosFiltrados.filter(
    (c) => c.moneda_origen === "USD" && c.moneda_destino === "ARS"
  );

  const totalARStoUSD = cambiosARStoUSD.reduce(
    (sum, c) => sum + parseFloat(c.monto_origen),
    0
  );
  const totalUSDtoARS = cambiosUSDtoARS.reduce(
    (sum, c) => sum + parseFloat(c.monto_origen),
    0
  );

  const cambiosEsteMes = cambiosFiltrados.filter(
    (c) => c.fecha.substring(0, 7) === new Date().toISOString().substring(0, 7)
  );

  // Prevenir scroll shift cuando el modal está abierto
  usePreventScrollShift(showModal);

  return (
    <div
      className="main-container"
      style={{
        padding: SPACING.containerPadding(isMobile),
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* ===== HEADER UNIFICADO ===== */}
      <div
        className="header-section"
        style={{
          marginBottom: SPACING.headerMargin,
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: SPACING.buttonGap,
            marginBottom: SPACING.headerMargin,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: colorVista.gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 16px ${colorVista.light}`,
              }}
            >
              <ArrowRightLeft size={20} color="white" />
            </div>
            <div>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                {isCliente ? "Tus Cambios" : "Cambios"}
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                }}
              >
                {isCliente
                  ? `Cambios de moneda de tu proyecto ${
                      permisosCliente?.proyectoCodigo || ""
                    }`
                  : "Operaciones de cambio de moneda"}
              </p>
            </div>
          </div>

          {/* Botón desktop unificado */}
          {!isMobile && puedeCrear && (
            <button
              onClick={() => {
                setEditingCambio(null);
                setShowModal(true);
              }}
              className="button-hover"
              style={{
                background: colorVista.gradient,
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                boxShadow: `0 4px 16px ${colorVista.light}`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              <Plus size={16} />
              Nuevo cambio
            </button>
          )}
        </div>
      </div>

      {/* Búsqueda y Filtros */}
      <div style={{ 
        marginBottom: "20px",
        display: isMobile ? "grid" : "flex",
        gridTemplateColumns: isMobile ? "1fr auto" : "none",
        gap: "12px",
        alignItems: "center",
        flexWrap: "wrap"
      }}>
        {/* Campo de búsqueda */}
        <div style={{ 
          flex: isMobile ? "none" : "1 1 auto",
          minWidth: isMobile ? "0" : "200px", 
          marginBottom: "0"
        }}>
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#64748b",
                zIndex: 1,
              }}
            />
            <input
              type="text"
              placeholder="Buscar por proyecto, cliente, proveedor o descripción..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={false}
              autoComplete="off"
              style={{
                width: "100%",
                padding: isMobile
                  ? "12px 16px 12px 44px"
                  : "10px 10px 10px 36px",
                border: "2px solid #f1f5f9",
                borderRadius: "12px",
                fontSize: isMobile ? "16px" : "14px",
                WebkitAppearance: "none",
                appearance: "none",
                background: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                outline: "none",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#3b82f6";
                e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#f1f5f9";
                e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
              }}
            />
          </div>
        </div>

        {/* Botón de Filtros - Solo mobile */}
        {isMobile && (
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            style={{
              padding: "12px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              background: showFilterPanel ? "#f1f5f9" : (hayFiltrosActivos() ? "#fef3c7" : "white"),
              color: showFilterPanel ? "#1e293b" : (hayFiltrosActivos() ? "#92400e" : "#475569"),
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              minWidth: "44px",
              minHeight: "44px",
              position: "relative",
            }}
          >
            <Settings size={18} />
            {hayFiltrosActivos() && (
              <div
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#f59e0b",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </button>
        )}

        {/* Botón de Filtros - Solo desktop */}
        {!isMobile && (
          <button
            onClick={() => setShowFilterPanel(!showFilterPanel)}
            style={{
              background: showFilterPanel ? "#f1f5f9" : (hayFiltrosActivos() ? "#fef3c7" : "white"),
              color: showFilterPanel ? "#1e293b" : (hayFiltrosActivos() ? "#92400e" : "#475569"),
              border: "2px solid #f1f5f9",
              borderRadius: "12px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s ease",
              position: "relative",
              minHeight: "44px",
              touchAction: "manipulation",
            }}
          >
            <Settings size={16} />
            Filtros
            {hayFiltrosActivos() && (
              <div
                style={{
                  position: "absolute",
                  top: "-4px",
                  right: "-4px",
                  width: "8px",
                  height: "8px",
                  backgroundColor: "#f59e0b",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </button>
        )}
      </div>

      {/* Panel de Filtros Desplegable */}
      <div
        style={{
          maxHeight: showFilterPanel ? (isMobile ? "500px" : "100px") : "0px",
          overflow: showFilterPanel ? "visible" : "hidden",
          transition: "all 0.25s ease-out",
          opacity: showFilterPanel ? 1 : 0,
          marginBottom: showFilterPanel ? "20px" : "0px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile 
              ? "1fr" 
              : isCliente
              ? "1fr 1fr 1fr"
              : "1fr 1fr 1fr 1fr",
            gap: isMobile ? "8px" : "12px",
            alignItems: "center",
            transform: `translateY(${showFilterPanel ? '0' : '-8px'})`,
            transition: "transform 0.25s ease-out",
            padding: isMobile && showFilterPanel ? "12px 0" : "0",
          }}
        >
          {/* Filtro de Proyecto - Solo para constructoras */}
          {!isCliente && (
            <CustomDropdown
              value={filterProject}
              onChange={(value) => {
                setFilterProject(value);
                guardarFiltroProyecto(value);
              }}
              options={[
                { value: "todos", label: "Todos los proyectos" },
                ...proyectos.map((proyecto) => ({
                  value: proyecto.id,
                  label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
                })),
              ]}
              placeholder="Seleccionar proyecto"
              icon={Building2}
            />
          )}

          {/* Filtro de Mes */}
          <CustomDropdown
            value={filterMonth}
            onChange={setFilterMonth}
            options={[
              { value: "todos", label: "Todos los meses" },
              ...mesesUnicos.map((mes) => ({
                value: mes,
                label: formatMonthForSelect(mes),
              })),
            ]}
            placeholder="Seleccionar mes"
            icon={Calendar}
          />

          {/* Filtro de Estado */}
          <CustomDropdown
            value={filterEstado}
            onChange={setFilterEstado}
            options={[
              { value: 'activos', label: 'Activos' }, 
              { value: 'eliminados', label: 'Eliminados' }
            ]}
            placeholder="Estado"
            icon={Filter}
          />
        </div>
      </div>

      {/* ===== TARJETAS RESUMEN COMPACTAS (SOLO DESKTOP) ===== */}
      {!isMobile && (
        <div
          className="stats-section"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: SPACING.statsGap,
            marginBottom: SPACING.sectionMargin,
            animation: "fadeInUp 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {[
            {
              label: "Total Cambios",
              value: cambiosFiltrados.length.toLocaleString(),
              icon: ArrowRightLeft,
              color: colorVista.solid,
            },
            {
              label: "Este mes",
              value: cambiosEsteMes.length.toLocaleString(),
              icon: Calendar,
              color: "#10b981",
            },
            {
              label: "ARS → USD",
              value: `${cambiosARStoUSD.length} cambios`,
              subtitle: `$${totalARStoUSD.toLocaleString()} ARS`,
              icon: TrendingUp,
              color: "#0ea5e9",
            },
            {
              label: "USD → ARS",
              value: `${cambiosUSDtoARS.length} cambios`,
              subtitle: `US$${totalUSDtoARS.toLocaleString()} USD`,
              icon: TrendingDown,
              color: "#8b5cf6",
            },
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="stat-card"
                style={{
                  background: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  textAlign: "center",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  minHeight: "80px",
                  animation: `slideInRight 0.${
                    8 + index
                  }s cubic-bezier(0.4, 0, 0.2, 1)`,
                }}
              >
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    background: `${stat.color}10`,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                  }}
                >
                  <IconComponent size={16} color={stat.color} />
                </div>
                <p
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    margin: "0 0 4px 0",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {stat.label}
                </p>
                <p
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: "#1e293b",
                    margin: 0,
                  }}
                >
                  {stat.value}
                </p>
                {stat.subtitle && (
                  <p
                    style={{
                      fontSize: "10px",
                      color: "#64748b",
                      margin: "2px 0 0 0",
                      fontWeight: "500",
                    }}
                  >
                    {stat.subtitle}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ===== CONTENIDO PRINCIPAL ===== */}
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : cambiosFiltrados.length === 0 ? (
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: isMobile ? "40px 24px" : "80px 40px",
            textAlign: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            // border eliminado para quitar la línea punteada
          }}
        >
          <RefreshCw
            size={48}
            color="#64748b"
            style={{ marginBottom: "20px" }}
          />
          <h3
            style={{
              fontSize: isMobile ? "18px" : "20px",
              color: "#1e293b",
              marginBottom: "10px",
              fontWeight: "600",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "No se encontraron cambios"
              : isCliente
              ? "No tienes cambios registrados"
              : "No hay cambios aún"}
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "15px",
              marginBottom:
                searchTerm ||
                filterProject !== "todos" ||
                filterMonth !== "todos"
                  ? "0"
                  : "25px",
            }}
          >
            {searchTerm || filterProject !== "todos" || filterMonth !== "todos"
              ? "Intenta cambiar los filtros de búsqueda"
              : isCliente
              ? "Los cambios de moneda aparecerán cuando se registren operaciones relacionadas con tu proyecto"
              : "Comienza registrando tu primer cambio de moneda"}
          </p>
          {!searchTerm &&
            filterProject === "todos" &&
            filterMonth === "todos" &&
            puedeCrear && (
              <button
                onClick={() => {
                  setEditingCambio(null);
                  setShowModal(true);
                }}
                style={{
                  background: colorVista.gradient,
                  color: "white",
                  border: "none",
                  padding: "14px 28px",
                  borderRadius: "10px",
                  fontSize: "15px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  margin: "0 auto",
                }}
              >
                <Plus size={16} />
                Crear Primer Cambio
              </button>
            )}
        </div>
      ) : /* ===== VISTA MÓVIL - CARDS ===== */
      isMobile && isClient ? (
        <div style={{ padding: "8px" }}>
          {cambiosFiltrados.map((cambio, index) => (
            <div
              key={cambio.id}
              onClick={() => openDetailModal(cambio)}
              style={{
                background: "white",
                border: "1px solid #f1f5f9",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "8px",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                animation: `slideInRight 0.${
                  5 + (index % 10)
                }s cubic-bezier(0.4, 0, 0.2, 1)`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                touchAction: "manipulation",
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.transform = "scale(0.98)";
                e.currentTarget.style.backgroundColor = "#f8fafc";
              }}
              onTouchEnd={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.backgroundColor = "white";
              }}
            >
              {/* HEADER UNIFICADO */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#1e293b",
                      marginBottom: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <ArrowRightLeft size={14} color={colorVista.solid} />
                    {cambio.proyectos?.codigo}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    {cambio.proyectos?.apellido_cliente}
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "right",
                    marginLeft: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "4px",
                      marginBottom: "2px",
                    }}
                  >
                    <Calendar size={10} />
                    {formatDateForDisplay(cambio.fecha)}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "600",
                      color: colorVista.solid,
                    }}
                  >
                    TC: ${Number(cambio.tipo_cambio).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* OPERACIÓN DE CAMBIO */}
              <div
                style={{
                  background: colorVista.light,
                  border: `1px solid ${colorVista.solid}20`,
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  <span style={{ color: "#dc2626" }}>
                    {cambio.moneda_origen}{" "}
                    {Number(cambio.monto_origen).toLocaleString()}
                  </span>
                  <ArrowRightLeft size={12} color={colorVista.solid} />
                  <span style={{ color: "#059669" }}>
                    {cambio.moneda_destino}{" "}
                    {Number(cambio.monto_destino).toLocaleString()}
                  </span>
                </div>

                {/* Cajas */}
                <div
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    textAlign: "center",
                    marginTop: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                  }}
                >
                  <Wallet size={8} />
                  {generarNombreCaja(cambio.caja_origen)} →{" "}
                  {generarNombreCaja(cambio.caja_destino)}
                </div>
              </div>

              {/* BADGES UNIFICADOS */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "12px",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    background: "#eff6ff",
                    color: "#3b82f6",
                    padding: "4px 8px",
                    borderRadius: "6px",
                    fontSize: "11px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <User size={8} />
                  {cambio.proveedores_cambio?.nombre}
                </span>

                {cambio.tiene_comprobante && (
                  <span
                    style={{
                      background: "#dcfce7",
                      color: "#166534",
                      padding: "4px 8px",
                      borderRadius: "6px",
                      fontSize: "11px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <FileText size={8} />
                    Comprobante
                  </span>
                )}
              </div>

              {/* DESCRIPCIÓN (si existe) */}
              {cambio.descripcion && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "12px",
                    lineHeight: "1.4",
                    fontStyle: "italic",
                  }}
                >
                  "{cambio.descripcion}"
                </div>
              )}

              {/* FOOTER UNIFICADO */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: "12px",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: "500",
                  }}
                >
                  Toca para ver detalles
                </div>

                <div style={{ display: "flex", gap: "4px" }}>
                  {puedeEditar && filterEstado === 'activos' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCambio(cambio);
                        setShowModal(true);
                      }}
                      style={{
                        background: "#eff6ff",
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 8px",
                        cursor: "pointer",
                        color: "#3b82f6",
                        touchAction: "manipulation",
                      }}
                    >
                      <Edit size={12} />
                    </button>
                  )}
                  {puedeEliminar && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (filterEstado === 'activos') handleEliminar(cambio.id);
                        else handleRestaurar(cambio.id);
                      }}
                      style={{
                        background: filterEstado === 'activos' ? "#fef2f2" : '#f0fdf4',
                        border: "none",
                        borderRadius: "6px",
                        padding: "6px 8px",
                        cursor: "pointer",
                        color: filterEstado === 'activos' ? "#ef4444" : '#22c55e',
                        touchAction: "manipulation",
                      }}
                    >
                      {filterEstado === 'activos' ? <Trash2 size={12} /> : <Package size={12} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ===== VISTA DESKTOP - TABLA ===== */
        <div
          style={{
            background: "white",
            borderRadius: SPACING.borderRadius.large,
            overflow: "hidden",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            animation: "fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "1200px",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Fecha
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Proyecto
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Cambio
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "right",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Tipo Cambio
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Proveedor
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "left",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                    }}
                  >
                    Archivos
                  </th>
                  <th
                    style={{
                      padding: "16px 20px",
                      textAlign: "center",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "14px",
                      width: "160px",
                    }}
                  >
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {cambiosFiltrados.map((cambio, index) => (
                  <tr
                    key={cambio.id}
                    style={{
                      borderBottom:
                        index < cambiosFiltrados.length - 1
                          ? "1px solid #f1f5f9"
                          : "none",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.parentElement.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      e.target.parentElement.style.backgroundColor =
                        "transparent";
                    }}
                  >
                    <td
                      style={{
                        padding: "16px 20px",
                        color: "#374151",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <Calendar size={14} color="#64748b" />
                        {formatDateForDisplay(cambio.fecha)}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        color: "#1e293b",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Building2 size={14} color={colorVista.solid} />
                          {cambio.proyectos?.codigo}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#64748b",
                            fontWeight: "400",
                            marginTop: "2px",
                          }}
                        >
                          {cambio.proyectos?.apellido_cliente}
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        color: "#1e293b",
                        fontSize: "13px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "6px",
                        }}
                      >
                        {/* Cajas */}
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#64748b",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Wallet size={12} />
                          {generarNombreCaja(cambio.caja_origen)} →{" "}
                          {generarNombreCaja(cambio.caja_destino)}
                        </div>

                        {/* Montos */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: "600",
                          }}
                        >
                          <span style={{ color: "#dc2626" }}>
                            {cambio.moneda_origen}{" "}
                            {Number(cambio.monto_origen).toLocaleString()}
                          </span>
                          <ArrowRightLeft size={12} color={colorVista.solid} />
                          <span style={{ color: "#059669" }}>
                            {cambio.moneda_destino}{" "}
                            {Number(cambio.monto_destino).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "right",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: colorVista.solid,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "4px",
                        }}
                      >
                        <DollarSign size={14} />
                        {Number(cambio.tipo_cambio).toLocaleString()}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        color: "#64748b",
                        fontSize: "13px",
                      }}
                    >
                      <span
                        style={{
                          background: "#fef3cd",
                          color: "#92400e",
                          padding: "4px 8px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "500",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <User size={10} />
                        {cambio.proveedores_cambio?.nombre}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        color: "#64748b",
                        fontSize: "13px",
                      }}
                    >
                      {cambio.tiene_comprobante && (
                        <span
                          onClick={() =>
                            openDocumentModal(cambio.comprobante_url, cambio)
                          }
                          className="action-button"
                          style={{
                            background: "#dcfce7",
                            color: "#166534",
                            padding: "4px 8px",
                            borderRadius: "8px",
                            fontSize: "10px",
                            fontWeight: "500",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            border: "1px solid transparent",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#bbf7d0";
                            e.target.style.border = "1px solid #166534";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#dcfce7";
                            e.target.style.border = "1px solid transparent";
                          }}
                        >
                          <FileText size={10} />
                          Comprobante
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                        }}
                      >
                        {/* Botón para ver detalles completos - SIEMPRE VISIBLE */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailModal(cambio);
                          }}
                          className="action-button"
                          style={{
                            background: "#eff6ff",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px 8px",
                            cursor: "pointer",
                            color: "#1d4ed8",
                            fontSize: "12px",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = "#dbeafe";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = "#eff6ff";
                          }}
                        >
                          <Eye size={14} />
                        </button>

                        {/* Solo mostrar editar/eliminar si tiene permisos */}
                        {puedeEditar && filterEstado === 'activos' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingCambio(cambio);
                              setShowModal(true);
                            }}
                            className="action-button"
                            style={{
                              background: "#f1f5f9",
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: "#475569",
                              fontSize: "12px",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "#e2e8f0";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "#f1f5f9";
                            }}
                          >
                            <Edit size={14} />
                          </button>
                        )}
                        {puedeEliminar && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (filterEstado === 'activos') handleEliminar(cambio.id);
                              else handleRestaurar(cambio.id);
                            }}
                            className="action-button"
                            style={{
                              background: filterEstado === 'activos' ? "#fef2f2" : '#f0fdf4',
                              border: "none",
                              borderRadius: "6px",
                              padding: "6px 8px",
                              cursor: "pointer",
                              color: filterEstado === 'activos' ? "#dc2626" : '#22c55e',
                              fontSize: "12px",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = filterEstado === 'activos' ? "#fee2e2" : '#dcfce7';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = filterEstado === 'activos' ? "#fef2f2" : '#f0fdf4';
                            }}
                          >
                            {filterEstado === 'activos' ? <Trash2 size={14} /> : <Package size={14} />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== BOTÓN FLOTANTE MÓVIL UNIFICADO ===== */}
      {isMobile && puedeCrear && (
        <div
          style={{
            position: "fixed",
            bottom: "100px",
            right: "20px",
            zIndex: 1000,
          }}
        >
          <button
            onClick={() => {
              setEditingCambio(null);
              setShowModal(true);
            }}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "28px",
              background: colorVista.gradient,
              color: "white",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              boxShadow: `0 8px 32px ${colorVista.light}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.transform = "scale(0.95)";
            }}
            onTouchEnd={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <Plus size={24} />
          </button>
        </div>
      )}

      {/* ===== MODALES ===== */}

      {/* Modal para crear/editar cambios - Solo para usuarios con permisos */}
      {showModal && puedeCrear && (
        <CambioModal
          constructora={constructora}
          usuario={usuario}
          cambio={editingCambio}
          proyectos={proyectos}
          cajas={cajas}
          proveedoresCambio={proveedoresCambio}
          setProveedoresCambio={setProveedoresCambio}
          onClose={() => {
            setShowModal(false);
            setEditingCambio(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingCambio(null);
            cargarDatos();
            showCustomAlert(
              "success",
              "Éxito",
              editingCambio
                ? "Cambio actualizado correctamente"
                : "Cambio creado correctamente"
            );
          }}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
          permisosCliente={permisosCliente}
          colorVista={colorVista}
        />
      )}

      {/* Modal de Vista Detallada */}
      {showDetailModal && selectedCambioDetail && (
        <CambioDetailModal
          cambio={selectedCambioDetail}
          proyectos={proyectos}
          cajas={cajas}
          proveedoresCambio={proveedoresCambio}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCambioDetail(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal de Documento Individual */}
      {showDocumentModal && selectedDocument && (
        <DocumentViewModal
          url={selectedDocument}
          onClose={() => {
            setShowDocumentModal(false);
            setSelectedDocument(null);
          }}
          isMobile={isMobile}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      {/* ===== CSS OBLIGATORIO ===== */}
      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        /* Hover effects */
        .button-hover:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .action-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}</style>
    </div>
  );
}

// ===== MODAL PARA CREAR/EDITAR CAMBIOS =====
function CambioModal({
  constructora,
  usuario,
  cambio,
  proyectos,
  cajas,
  proveedoresCambio,
  setProveedoresCambio,
  onClose,
  onSave,
  isMobile,
  showCustomAlert,
  permisosCliente,
  colorVista,
}) {
  const [formData, setFormData] = useState({
    proyecto_id: "",
    caja_origen_id: "",
    caja_destino_id: "",
    proveedor_cambio_id: "",
    monto_origen: "",
    moneda_origen: "",
    monto_destino: "",
    moneda_destino: "",
    tipo_cambio: "",
    descripcion: "",
    fecha: new Date().toISOString().split("T")[0],
    comprobante_url: "",
    tiene_comprobante: false,
  });

  const [guardando, setGuardando] = useState(false);
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [dolarBlue, setDolarBlue] = useState(null);
  const [loadingDolar, setLoadingDolar] = useState(false);
  const [showNewProveedorModal, setShowNewProveedorModal] = useState(false);
  const [nuevoProveedor, setNuevoProveedor] = useState("");
  const [guardandoProveedor, setGuardandoProveedor] = useState(false);
  const [saldoInsuficiente, setSaldoInsuficiente] = useState(false);

  // Cajas disponibles según el proyecto seleccionado
  const [cajasDisponibles, setCajasDisponibles] = useState([]);

  // Manejar Escape para cerrar modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  useEffect(() => {
    if (cambio) {
      setFormData({
        proyecto_id: cambio.proyecto_id || "",
        caja_origen_id: cambio.caja_origen_id || "",
        caja_destino_id: cambio.caja_destino_id || "",
        proveedor_cambio_id: cambio.proveedor_cambio_id || "",
        monto_origen: cambio.monto_origen || "",
        moneda_origen: cambio.moneda_origen || "",
        monto_destino: cambio.monto_destino || "",
        moneda_destino: cambio.moneda_destino || "",
        tipo_cambio: cambio.tipo_cambio || "",
        descripcion: cambio.descripcion || "",
        fecha: formatDateForInput(cambio.fecha),
        comprobante_url: cambio.comprobante_url || "",
        tiene_comprobante: cambio.tiene_comprobante || false,
      });
    } else {
      // Si es cliente, preseleccionar su proyecto
      if (permisosCliente?.proyectoId) {
        setFormData((prev) => ({
          ...prev,
          proyecto_id: permisosCliente.proyectoId,
        }));
      }
    }
  }, [cambio, permisosCliente]);

  // Cargar cotización del dólar blue al abrir el modal
  useEffect(() => {
    cargarDolarBlue();
  }, []);

  // Actualizar cajas disponibles cuando cambia el proyecto
  useEffect(() => {
    if (formData.proyecto_id) {
      actualizarCajasDisponibles();
    } else {
      setCajasDisponibles([]);
    }
  }, [formData.proyecto_id, cajas]);

  // Auto-seleccionar caja destino cuando se selecciona origen
  useEffect(() => {
    if (formData.caja_origen_id) {
      const cajaOrigen = cajas.find((c) => c.id === formData.caja_origen_id);
      if (cajaOrigen) {
        setFormData((prev) => ({
          ...prev,
          moneda_origen: cajaOrigen.moneda,
        }));

        // Buscar caja destino automáticamente
        buscarCajaDestino(cajaOrigen);

        // Actualizar tipo de cambio según la moneda origen
        actualizarTipoCambioSegunMoneda(cajaOrigen.moneda);
      }
    }
  }, [formData.caja_origen_id, cajas, dolarBlue]);

  // Calcular monto destino cuando cambia monto origen o tipo cambio
  useEffect(() => {
    if (
      formData.monto_origen &&
      formData.tipo_cambio &&
      formData.moneda_origen
    ) {
      calcularMontoDestino();
    }
  }, [formData.monto_origen, formData.tipo_cambio, formData.moneda_origen]);

  // Verificar saldo suficiente
  useEffect(() => {
    if (formData.caja_origen_id && formData.monto_origen) {
      verificarSaldoSuficiente();
    } else {
      setSaldoInsuficiente(false);
    }
  }, [formData.caja_origen_id, formData.monto_origen, cajas]);

  const cargarDolarBlue = async () => {
    setLoadingDolar(true);
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/blue");
      const data = await response.json();
      setDolarBlue(data);
      console.log("Dólar blue cargado:", data);
    } catch (error) {
      console.error("Error cargando dólar blue:", error);
      // Fallback si no se puede obtener la cotización
      setDolarBlue({ compra: 1000, venta: 1020 });
    }
    setLoadingDolar(false);
  };

  const actualizarCajasDisponibles = () => {
    const cajasProyecto = cajas.filter((caja) => {
      // Cajas de empresa (siempre disponibles)
      if (caja.tipo_caja === "empresa") return true;

      // Cajas del proyecto seleccionado
      if (caja.proyecto_id === formData.proyecto_id) return true;

      return false;
    });

    // Ordenar: empresa primero, luego proyecto, luego mano obra
    const cajasOrdenadas = cajasProyecto.sort((a, b) => {
      if (a.tipo_caja === "empresa" && b.tipo_caja !== "empresa") return -1;
      if (a.tipo_caja !== "empresa" && b.tipo_caja === "empresa") return 1;
      if (a.tipo_caja === "proyecto" && b.tipo_caja === "mano_obra") return -1;
      if (a.tipo_caja === "mano_obra" && b.tipo_caja === "proyecto") return 1;
      return a.moneda.localeCompare(b.moneda);
    });

    setCajasDisponibles(cajasOrdenadas);
  };

  const buscarCajaDestino = (cajaOrigen) => {
    const monedaDestino = cajaOrigen.moneda === "ARS" ? "USD" : "ARS";

    // Buscar caja del mismo tipo pero diferente moneda
    const cajaDestino = cajas.find(
      (caja) =>
        caja.tipo_caja === cajaOrigen.tipo_caja &&
        caja.moneda === monedaDestino &&
        (cajaOrigen.tipo_caja === "empresa" ||
          caja.proyecto_id === cajaOrigen.proyecto_id)
    );

    if (cajaDestino) {
      setFormData((prev) => ({
        ...prev,
        caja_destino_id: cajaDestino.id,
        moneda_destino: cajaDestino.moneda,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        caja_destino_id: "",
        moneda_destino: monedaDestino,
      }));
    }
  };

  const actualizarTipoCambioSegunMoneda = (monedaOrigen) => {
    if (!dolarBlue) return;

    let tipoCambio;
    if (monedaOrigen === "ARS") {
      // ARS → USD: uso dólar blue VENTA (más caro)
      tipoCambio = dolarBlue.venta;
    } else {
      // USD → ARS: uso dólar blue COMPRA (más barato)
      tipoCambio = dolarBlue.compra;
    }

    setFormData((prev) => ({
      ...prev,
      tipo_cambio: tipoCambio.toString(),
    }));
  };

  const calcularMontoDestino = () => {
    const montoOrigen = parseFloat(formData.monto_origen);
    const tipoCambio = parseFloat(formData.tipo_cambio);

    if (!montoOrigen || !tipoCambio) return;

    let montoDestino;
    if (formData.moneda_origen === "ARS") {
      // ARS → USD
      montoDestino = montoOrigen / tipoCambio;
    } else {
      // USD → ARS
      montoDestino = montoOrigen * tipoCambio;
    }

    setFormData((prev) => ({
      ...prev,
      monto_destino: montoDestino.toFixed(2),
    }));
  };

  const verificarSaldoSuficiente = () => {
    const cajaOrigen = cajas.find((c) => c.id === formData.caja_origen_id);
    const montoOperacion = parseFloat(formData.monto_origen);

    if (cajaOrigen && montoOperacion) {
      const saldoActual = parseFloat(cajaOrigen.balance_actual) || 0;
      setSaldoInsuficiente(montoOperacion > saldoActual);
    }
  };

  const generarNombreCaja = (caja) => {
    return `${caja.nombre} ${caja.moneda}`;
  };

  const handleFileUpload = async (file, tipo) => {
    if (!file) return;

    if (!file.type.startsWith("image/") && !file.type.includes("pdf")) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor selecciona un archivo de imagen o PDF"
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showCustomAlert(
        "error",
        "Error",
        "El archivo es muy grande. Máximo 10MB"
      );
      return;
    }

    setSubiendoComprobante(true);

    try {
      const fileExt = file.name.split(".").pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = `cambio-${constructora.id}-${timestamp}.${fileExt}`;

      console.log("Subiendo comprobante cambio:", fileName);

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("comprobantes")
        .upload(fileName, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: "3600",
        });

      if (uploadError) {
        console.error("Error en upload comprobante:", uploadError);
        throw uploadError;
      }

      console.log("Upload comprobante exitoso:", uploadData);

      const { data: urlData } = supabase.storage
        .from("comprobantes")
        .getPublicUrl(fileName);

      const finalUrl = urlData.publicUrl;
      console.log("URL comprobante generada:", finalUrl);

      setFormData((prev) => ({
        ...prev,
        comprobante_url: finalUrl,
        tiene_comprobante: true,
      }));
    } catch (err) {
      console.error("Error subiendo comprobante:", err);
      showCustomAlert(
        "error",
        "Error",
        "Error al subir comprobante: " + err.message
      );
    }

    setSubiendoComprobante(false);
  };

  const crearNuevoProveedor = async (nombre) => {
    try {
      const { data, error } = await supabase
        .from("proveedores_cambio")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nombre.trim(),
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando proveedor:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear proveedor: " + error.message
        );
      } else {
        const nuevoProveedor = data[0];
        showCustomAlert("success", "Éxito", "Proveedor creado correctamente");
        
        // Actualizar la lista de proveedores
        setProveedoresCambio((prev) => [...prev, nuevoProveedor]);
        
        // Seleccionar el nuevo proveedor
        setFormData((prev) => ({
          ...prev,
          proveedor_cambio_id: nuevoProveedor.id,
        }));
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const agregarNuevoProveedor = async () => {
    if (!nuevoProveedor.trim()) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor ingresa un nombre para el proveedor"
      );
      return;
    }

    setGuardandoProveedor(true);
    try {
      const { data, error } = await supabase
        .from("proveedores_cambio")
        .insert([
          {
            constructora_id: constructora.id,
            nombre: nuevoProveedor.trim(),
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error guardando proveedor:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al guardar proveedor: " + error.message
        );
      } else {
        showCustomAlert("success", "Éxito", "Proveedor agregado correctamente");
        setNuevoProveedor("");
        setShowNewProveedorModal(false);

        // Seleccionar el nuevo proveedor
        const nuevoProveedorId = data[0].id;
        setFormData((prev) => ({
          ...prev,
          proveedor_cambio_id: nuevoProveedorId,
        }));

        // Recargar página para actualizar proveedores
        window.location.reload();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardandoProveedor(false);
  };

  const handleSave = async () => {
    // Validaciones
    if (
      !formData.proyecto_id ||
      !formData.caja_origen_id ||
      !formData.caja_destino_id ||
      !formData.proveedor_cambio_id ||
      !formData.monto_origen ||
      !formData.tipo_cambio ||
      !formData.fecha
    ) {
      showCustomAlert(
        "error",
        "Error",
        "Por favor completa todos los campos obligatorios"
      );
      return;
    }

    if (parseFloat(formData.monto_origen) <= 0) {
      showCustomAlert("error", "Error", "El monto origen debe ser mayor a 0");
      return;
    }

    if (parseFloat(formData.tipo_cambio) <= 0) {
      showCustomAlert("error", "Error", "El tipo de cambio debe ser mayor a 0");
      return;
    }

    if (formData.caja_origen_id === formData.caja_destino_id) {
      showCustomAlert(
        "error",
        "Error",
        "La caja origen y destino no pueden ser la misma"
      );
      return;
    }

    setGuardando(true);

    try {
      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: formData.proyecto_id,
        caja_origen_id: formData.caja_origen_id,
        caja_destino_id: formData.caja_destino_id,
        proveedor_cambio_id: formData.proveedor_cambio_id,
        monto_origen: parseFloat(formData.monto_origen),
        moneda_origen: formData.moneda_origen,
        monto_destino: parseFloat(formData.monto_destino),
        moneda_destino: formData.moneda_destino,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        descripcion: formData.descripcion.trim() || null,
        fecha: formData.fecha,
        comprobante_url: formData.comprobante_url || null,
        tiene_comprobante: formData.tiene_comprobante,
      };

      // Agregar campos de auditoría según si es creación o actualización
      if (cambio) {
        // Actualización: solo updated_by
        dataToSave.updated_by = usuario.id;
        console.log("📝 Actualizando cambio - updated_by:", usuario.id);
      } else {
        // Creación: created_by y updated_by
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        console.log("🆕 Creando cambio - created_by:", usuario.id, "updated_by:", usuario.id);
      }

      let result;
      if (cambio) {
        // Actualizar
        result = await supabase
          .from("cambios_moneda")
          .update(dataToSave)
          .eq("id", cambio.id)
          .select();
      } else {
        // Crear nuevo
        result = await supabase
          .from("cambios_moneda")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando cambio:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        // Actualizar balances de cajas
        await actualizarBalanceCajas();

        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }

    setGuardando(false);
  };

  const actualizarBalanceCajas = async () => {
    try {
      const montoOrigen = parseFloat(formData.monto_origen);
      const montoDestino = parseFloat(formData.monto_destino);

      // SI ES UNA EDICIÓN, verificar si cambió algo relevante para balances
      if (cambio) {
        const montoOrigenOriginal = parseFloat(cambio.monto_origen);
        const montoDestinoOriginal = parseFloat(cambio.monto_destino);
        const cajaOrigenOriginalId = cambio.caja_origen_id;
        const cajaDestinoOriginalId = cambio.caja_destino_id;

        console.log("🔄 EDITANDO CAMBIO - Verificando cambios...");
        console.log("Monto Origen:", montoOrigenOriginal, "→", montoOrigen);
        console.log("Monto Destino:", montoDestinoOriginal, "→", montoDestino);
        console.log(
          "Caja Origen:",
          cajaOrigenOriginalId,
          "→",
          formData.caja_origen_id
        );
        console.log(
          "Caja Destino:",
          cajaDestinoOriginalId,
          "→",
          formData.caja_destino_id
        );

        // Si no cambió nada relevante para balances, NO hacer nada
        if (
          montoOrigen === montoOrigenOriginal &&
          montoDestino === montoDestinoOriginal &&
          formData.caja_origen_id === cajaOrigenOriginalId &&
          formData.caja_destino_id === cajaDestinoOriginalId
        ) {
          console.log("✅ No cambió montos ni cajas - NO actualizar balances");
          return;
        }

        console.log("⚠️ Cambió algo relevante - Actualizando balances...");

        // REVERTIR la operación original
        // 1. Sumar a caja origen original (revertir resta anterior)
        if (cajaOrigenOriginalId) {
          const { data: cajaOrigenOriginal, error: errorOrigenOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaOrigenOriginalId)
              .single();

          if (!errorOrigenOriginal && cajaOrigenOriginal) {
            const balanceOrigenOriginal =
              parseFloat(cajaOrigenOriginal.balance_actual) || 0;
            const balanceOrigenRevertido =
              balanceOrigenOriginal + montoOrigenOriginal; // SUMAR para revertir resta anterior

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceOrigenRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaOrigenOriginalId);

            console.log(
              `🔄 REVERTIDO ORIGEN: Caja ${balanceOrigenOriginal} + ${montoOrigenOriginal} = ${balanceOrigenRevertido}`
            );
          }
        }

        // 2. Restar de caja destino original (revertir suma anterior)
        if (cajaDestinoOriginalId) {
          const { data: cajaDestinoOriginal, error: errorDestinoOriginal } =
            await supabase
              .from("cajas")
              .select("balance_actual")
              .eq("id", cajaDestinoOriginalId)
              .single();

          if (!errorDestinoOriginal && cajaDestinoOriginal) {
            const balanceDestinoOriginal =
              parseFloat(cajaDestinoOriginal.balance_actual) || 0;
            const balanceDestinoRevertido =
              balanceDestinoOriginal - montoDestinoOriginal; // RESTAR para revertir suma anterior

            await supabase
              .from("cajas")
              .update({
                balance_actual: balanceDestinoRevertido,
                updated_at: new Date().toISOString(),
              })
              .eq("id", cajaDestinoOriginalId);

            console.log(
              `🔄 REVERTIDO DESTINO: Caja ${balanceDestinoOriginal} - ${montoDestinoOriginal} = ${balanceDestinoRevertido}`
            );
          }
        }
      } else {
        console.log("🆕 NUEVO CAMBIO - Aplicando operación...");
      }

      // Ahora aplicar la nueva operación (tanto para INSERT como UPDATE)

      // 1. Restar de caja origen
      if (formData.caja_origen_id) {
        const { data: cajaOrigen, error: errorOrigen } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_origen_id)
          .single();

        if (!errorOrigen && cajaOrigen) {
          const balanceActualOrigen =
            parseFloat(cajaOrigen.balance_actual) || 0;
          const nuevoBalanceOrigen = balanceActualOrigen - montoOrigen; // RESTAR monto origen

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceOrigen,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_origen_id);

          console.log(
            `✅ APLICADO ORIGEN: Caja ${balanceActualOrigen} - ${montoOrigen} = ${nuevoBalanceOrigen} (${formData.moneda_origen})`
          );
        }
      }

      // 2. Sumar a caja destino
      if (formData.caja_destino_id) {
        const { data: cajaDestino, error: errorDestino } = await supabase
          .from("cajas")
          .select("balance_actual")
          .eq("id", formData.caja_destino_id)
          .single();

        if (!errorDestino && cajaDestino) {
          const balanceActualDestino =
            parseFloat(cajaDestino.balance_actual) || 0;
          const nuevoBalanceDestino = balanceActualDestino + montoDestino; // SUMAR monto destino

          await supabase
            .from("cajas")
            .update({
              balance_actual: nuevoBalanceDestino,
              updated_at: new Date().toISOString(),
            })
            .eq("id", formData.caja_destino_id);

          console.log(
            `✅ APLICADO DESTINO: Caja ${balanceActualDestino} + ${montoDestino} = ${nuevoBalanceDestino} (${formData.moneda_destino})`
          );
        }
      }
    } catch (err) {
      console.error("❌ Error en actualización de balances:", err);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
        animation: "fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: colorVista.gradient,
            color: "white",
            padding: isMobile ? "20px" : "25px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: 0,
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "rgba(255,255,255,0.2)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ArrowRightLeft size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  margin: "0 0 5px 0",
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cambio ? "Editar cambio" : "Nuevo cambio"}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  opacity: 0.9,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {cambio
                  ? "Modificar operación de cambio"
                  : "Registrar una nueva operación de cambio"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "10px",
              padding: "8px 12px",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              fontWeight: "600",
              flexShrink: 0,
              minWidth: "max-content",
              whiteSpace: "nowrap",
              touchAction: "manipulation",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <X size={16} />
          </button>
        </div>
        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "25px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: SPACING.formGap }}>
            {/* Proyecto - Campo principal */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Proyecto *
              </label>
              <CustomDropdown
                value={formData.proyecto_id}
                onChange={(value) =>
                  setFormData({ ...formData, proyecto_id: value })
                }
                options={proyectos.map((proyecto) => ({
                  value: proyecto.id,
                  label: `${proyecto.codigo} - ${proyecto.apellido_cliente} ${
                    proyecto.obra_finalizada ? "(Finalizado)" : "(Activo)"
                  }`,
                }))}
                placeholder="Selecciona un proyecto"
                disabled={permisosCliente?.proyectoId}
                icon={Building2}
              />
              {/* Mensaje para clientes */}
              {permisosCliente?.proyectoId && (
                <p
                  style={{
                    fontSize: "11px",
                    color: colorVista.solid,
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Info size={10} />
                  Proyecto preseleccionado: {permisosCliente.proyectoCodigo}
                </p>
              )}
            </div>

            {/* Caja Origen */}
            {cajasDisponibles.length > 0 && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Caja de origen *
                </label>
                <CustomDropdown
                  value={formData.caja_origen_id}
                  onChange={(value) =>
                    setFormData({ ...formData, caja_origen_id: value })
                  }
                  options={cajasDisponibles.map((caja) => ({
                    value: caja.id,
                    label: `${generarNombreCaja(caja)} ($${parseFloat(
                      caja.balance_actual || 0
                    ).toLocaleString()})`,
                  }))}
                  placeholder="Selecciona una caja origen"
                  icon={Wallet}
                />
              </div>
            )}

            {/* Caja Destino */}
            {formData.caja_origen_id && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Caja de Destino *
                </label>
                <CustomDropdown
                  value={formData.caja_destino_id}
                  onChange={(value) =>
                    setFormData({ ...formData, caja_destino_id: value })
                  }
                  options={cajasDisponibles
                    .filter((caja) => caja.id !== formData.caja_origen_id)
                    .map((caja) => ({
                      value: caja.id,
                      label: `${generarNombreCaja(caja)} ($${parseFloat(
                        caja.balance_actual || 0
                      ).toLocaleString()})`,
                    }))}
                  placeholder="Selecciona una caja destino"
                  icon={Wallet}
                />
                {formData.caja_destino_id === "" && formData.moneda_destino && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#dc2626",
                      margin: "4px 0 0 0",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <AlertCircle size={12} />
                    No existe una caja {formData.moneda_destino} correspondiente
                  </p>
                )}
              </div>
            )}

            {/* Monto Origen y Tipo de Cambio */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto a cambiar *
                </label>
                <div style={{ position: "relative" }}>
                  <DollarSign
                    size={16}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                    }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monto_origen}
                    onChange={(e) =>
                      setFormData({ ...formData, monto_origen: e.target.value })
                    }
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 40px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      outline: "none",
                      touchAction: "manipulation",
                    }}
                  />
                </div>
                {formData.moneda_origen && (
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      margin: "4px 0 0 0",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Info size={10} />
                    Moneda: {formData.moneda_origen}
                  </p>
                )}
                {saldoInsuficiente && (
                  <div
                    style={{
                      background: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      padding: "8px",
                      marginTop: "4px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "11px",
                        color: "#dc2626",
                        margin: 0,
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <AlertCircle size={10} />
                      El monto supera el saldo disponible. Se puede continuar
                      pero la caja quedará en negativo.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Tipo de cambio *{" "}
                  {loadingDolar && (
                    <Loader2 size={12} className="loading-pulse" />
                  )}
                </label>
                <div style={{ position: "relative" }}>
                  <DollarSign
                    size={16}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#64748b",
                    }}
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={formData.tipo_cambio}
                    onChange={(e) =>
                      setFormData({ ...formData, tipo_cambio: e.target.value })
                    }
                    placeholder="1.00"
                    style={{
                      width: "100%",
                      padding: "12px 16px 12px 40px",
                      border: "2px solid #f1f5f9",
                      borderRadius: "12px",
                      fontSize: "16px",
                      boxSizing: "border-box",
                      outline: "none",
                      touchAction: "manipulation",
                    }}
                  />
                </div>
                {dolarBlue && formData.moneda_origen && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#059669",
                      margin: "4px 0 0 0",
                      fontWeight: "500",
                    }}
                  >
                    <p
                      style={{
                        margin: "2px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <TrendingUp size={10} />
                      Blue Compra: ${dolarBlue.compra} | Venta: $
                      {dolarBlue.venta}
                    </p>
                    <p
                      style={{
                        margin: "2px 0",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <ArrowRightLeft size={10} />
                      {formData.moneda_origen === "ARS"
                        ? `ARS→USD: Usando venta ($${dolarBlue.venta})`
                        : `USD→ARS: Usando compra ($${dolarBlue.compra})`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Monto Destino (calculado automáticamente) */}
            {formData.monto_destino && (
              <div
                style={{
                  background: colorVista.light,
                  border: `2px solid ${colorVista.solid}30`,
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorVista.solid,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <ArrowRightLeft size={14} />
                  Resultado del Cambio
                </h4>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    fontSize: "16px",
                    fontWeight: "700",
                  }}
                >
                  <span style={{ color: "#dc2626" }}>
                    {formData.moneda_origen}{" "}
                    {Number(formData.monto_origen).toLocaleString()}
                  </span>
                  <ArrowRightLeft size={16} color={colorVista.solid} />
                  <span style={{ color: "#059669" }}>
                    {formData.moneda_destino}{" "}
                    {Number(formData.monto_destino).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Proveedor de Cambio */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Proveedor de cambio *
                </label>
                {proveedoresCambio.length === 0 && (
                  <button
                    type="button"
                    onClick={() => setShowNewProveedorModal(true)}
                    style={{
                      background: "#fef3cd",
                      border: `1px solid ${colorVista.solid}50`,
                      borderRadius: "6px",
                      padding: "4px 8px",
                      fontSize: "11px",
                      color: colorVista.solid,
                      cursor: "pointer",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Plus size={10} />
                    Crear proveedor
                  </button>
                )}
              </div>

              {(() => {
                const activos = (proveedoresCambio || []).map(p => ({ value: p.id, label: p.nombre }));
                const yaIncluyeSeleccionado = (proveedoresCambio || []).some(p => p.id === formData.proveedor_cambio_id);
                let seleccionadoExtra = [];
                if (!yaIncluyeSeleccionado && formData.proveedor_cambio_id) {
                  const nombreSeleccionado = cambio?.proveedores_cambio?.nombre;
                  if (nombreSeleccionado) {
                    seleccionadoExtra = [{ value: formData.proveedor_cambio_id, label: nombreSeleccionado }];
                  } else {
                    seleccionadoExtra = [{ value: formData.proveedor_cambio_id, label: '(inactivo)' }];
                  }
                }
                const opcionesDropdown = [...activos, ...seleccionadoExtra];
                return (
                  <CustomDropdown
                    value={formData.proveedor_cambio_id}
                    onChange={(value) => setFormData({ ...formData, proveedor_cambio_id: value })}
                    options={opcionesDropdown}
                    placeholder="Selecciona un proveedor"
                    icon={User}
                    canCreate={true}
                    onCreateNew={crearNuevoProveedor}
                    createText="Crear proveedor"
                  />
                );
              })()}
            </div>

            {/* Fecha */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Fecha *
              </label>
              <div style={{ position: "relative" }}>
                <Calendar
                  size={16}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#64748b",
                  }}
                />
                <input
                  type="date"
                  value={formData.fecha}
                  onChange={(e) =>
                    setFormData({ ...formData, fecha: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "12px 16px 12px 40px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    touchAction: "manipulation",
                  }}
                />
              </div>
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción adicional del cambio..."
                rows="3"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  touchAction: "manipulation",
                }}
              />
            </div>

            {/* Comprobante */}
            <div
              style={{
                background: "#f8fafc",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                }}
              >
                <label
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <FileText size={14} />
                  Comprobante de Cambio
                </label>
                <button
                  type="button"
                  aria-pressed={Boolean(formData.tiene_comprobante)}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      tiene_comprobante: !prev.tiene_comprobante,
                      comprobante_url: !prev.tiene_comprobante ? prev.comprobante_url : "",
                    }))
                  }
                  style={{
                    width: 44,
                    height: 24,
                    borderRadius: 9999,
                    border: '1px solid ' + (formData.tiene_comprobante ? colorVista.solid : '#cbd5e1'),
                    background: formData.tiene_comprobante ? colorVista.solid : '#e2e8f0',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all .2s ease',
                    outline: 'none',
                  }}
                >
                  <span
                    style={{
                      position: 'absolute',
                      top: 2,
                      left: formData.tiene_comprobante ? 22 : 2,
                      width: 18,
                      height: 18,
                      borderRadius: '9999px',
                      background: 'white',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                      transition: 'left .2s ease',
                    }}
                  />
                </button>
              </div>

              {formData.tiene_comprobante && (
                <div
                  style={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "center",
                    background: "white",
                  }}
                >
                  {formData.comprobante_url ? (
                    <div>
                      <CheckCircle2
                        size={24}
                        color="#22c55e"
                        style={{ marginBottom: "8px" }}
                      />
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#22c55e",
                          margin: "0 0 8px 0",
                          fontWeight: "600",
                        }}
                      >
                        Comprobante subido
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          onClick={() =>
                            window.open(formData.comprobante_url, "_blank")
                          }
                          style={{
                            background: "none",
                            border: `1px solid ${colorVista.solid}`,
                            borderRadius: "6px",
                            padding: "4px 8px",
                            color: colorVista.solid,
                            fontSize: "11px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Eye size={10} />
                          Ver
                        </button>
                        <button
                          onClick={() =>
                            setFormData({ ...formData, comprobante_url: "" })
                          }
                          style={{
                            background: "none",
                            border: "1px solid #ef4444",
                            borderRadius: "6px",
                            padding: "4px 8px",
                            color: "#ef4444",
                            fontSize: "11px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Trash2 size={10} />
                          Quitar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        id="comprobante-cambio-upload"
                        accept="image/*,application/pdf"
                        onChange={(e) =>
                          handleFileUpload(e.target.files[0], "comprobante")
                        }
                        style={{ display: "none" }}
                        disabled={subiendoComprobante}
                      />
                      <label
                        htmlFor="comprobante-cambio-upload"
                        style={{
                          background: subiendoComprobante
                            ? "#94a3b8"
                            : colorVista.solid,
                          color: "white",
                          padding: "8px 12px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: subiendoComprobante
                            ? "not-allowed"
                            : "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {subiendoComprobante ? (
                          <>
                            <Loader2 size={12} className="loading-pulse" />
                            Subiendo...
                          </>
                        ) : (
                          <>
                            <Upload size={12} />
                            Seleccionar comprobante
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "15px 20px" : "20px 25px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={onClose}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 16px" : "12px 20px",
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <X size={16} />
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={guardando || subiendoComprobante}
            className="button-hover"
            style={{
              padding: isMobile ? "10px 20px" : "12px 24px",
              border: "none",
              borderRadius: "10px",
              background:
                guardando || subiendoComprobante
                  ? "#94a3b8"
                  : colorVista.gradient,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                guardando || subiendoComprobante ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              touchAction: "manipulation",
            }}
          >
            {guardando ? (
              <>
                <Loader2 size={16} className="loading-pulse" />
                Guardando...
              </>
            ) : (
              <>
                <Save size={16} />
                Guardar Cambio
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Cambios;
